// Latest orders: items from the data source; the widget refetches when a sync or webhook brings new
// orders, and any order.created events streamed over WebSocket are merged in (de-duplicated by id).
import { useEffect, useMemo, useState } from 'react';
import { formatValue, relativeTime } from '../../lib/format';
import type { RegistryEntry, WidgetComponentProps } from '../types';

interface Item {
  orderId: string;
  orderNumber: string;
  storeName: string;
  createdAt: string;
  total: number;
  currency: string;
  totalGbp: number | null;
  channel?: string;
  items?: number;
  firstItem?: string | null;
  via?: string;
}

function LiveFeed({ data, ctx }: WidgetComponentProps) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 10_000);
    return () => clearInterval(t);
  }, []);
  const streamed = useMemo(
    () => ctx.events.filter((e) => e.event === 'order.created').map((e) => ({ ...(e.payload as Item), _live: true })),
    [ctx.events],
  );
  const items = useMemo(() => {
    const seen = new Set<string>();
    return [...streamed, ...((data?.items ?? []) as Item[])]
      .filter((i) => (seen.has(i.orderId) ? false : (seen.add(i.orderId), true)))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .slice(0, 40);
  }, [streamed, data]);
  const liveIds = new Set(streamed.map((s) => s.orderId));
  if (!items.length) return <div className="state">No orders yet for this selection.</div>;
  return (
    <ul className="feed" aria-live="polite" aria-label="Live orders">
      {items.map((o) => (
        <li key={o.orderId} className={liveIds.has(o.orderId) ? 'new' : ''}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {o.storeName} <span className="muted" style={{ fontWeight: 400 }}>{o.orderNumber}</span>
            </div>
            <div className="meta">
              {relativeTime(o.createdAt)}
              {o.channel ? ` · ${o.channel}` : ''}
              {o.items ? ` · ${o.items} item${o.items > 1 ? 's' : ''}` : ''}
              {o.via ? ` · ${o.via}` : ''}
            </div>
          </div>
          <div>
            <div className="amt">{formatValue(o.total, 'currency', { currency: o.currency })}</div>
            {o.currency !== 'GBP' && <div className="meta" style={{ textAlign: 'right' }}>{o.totalGbp === null ? 'HMRC rate missing' : `≈ ${formatValue(o.totalGbp, 'currency', { currency: 'GBP' })}`}</div>}
          </div>
        </li>
      ))}
    </ul>
  );
}

export const liveFeed: RegistryEntry = {
  component: LiveFeed,
  toTable: (d) => ({
    columns: [
      { key: 'createdAt', label: 'Time', format: 'datetime' },
      { key: 'storeName', label: 'Store' },
      { key: 'orderNumber', label: 'Order' },
      { key: 'channel', label: 'Channel' },
      { key: 'total', label: 'Total', format: 'decimal', numeric: true },
      { key: 'currency', label: 'Currency' },
      { key: 'totalGbp', label: 'GBP', format: 'currency', currency: 'GBP', numeric: true },
    ],
    rows: d?.items ?? [],
  }),
};
